/**
 * RectangleZonePrimitive — user-drawn time+price rectangle annotations (item 3).
 * Modeled on OrderBlockPrimitive's canvas approach (timeToCoordinate ×
 * priceToCoordinate, bitmap space) but zOrder 'bottom' so candles stay readable
 * over the translucent fill.
 *
 * Publishes per-zone hit-test geometry in CSS px (PnlBadgePrimitive's lastRect
 * pattern): body drag moves the zone, top/bottom edge drags resize it — the
 * interaction handlers live in TradingChart and use hitTest(x, y).
 *
 * Time units: UNIX seconds (IST-shifted, matching the chart's time axis).
 * Zones are annotations — they are NOT clamped to replayMaxTime by design.
 */
import type {
  ISeriesPrimitive,
  IPrimitivePaneView,
  IPrimitivePaneRenderer,
  SeriesAttachedParameter,
  Time,
  IChartApi,
  ISeriesApi,
  SeriesType,
} from 'lightweight-charts';
import type { CanvasRenderingTarget2D } from 'fancy-canvas';

export interface RectZone {
  id: string;
  t1: number;   // left time (sec, IST-shifted) — normalized t1 < t2 on commit
  t2: number;   // right time
  p1: number;   // top price — normalized p1 > p2 on commit
  p2: number;   // bottom price
  color: string;    // base hex colour, e.g. '#2196f3'
  opacity: number;  // fill opacity 0.05–0.60
}

export type RectPart = 'body' | 'top' | 'bottom' | 'left' | 'right';
export interface RectHit { id: string; part: RectPart }

interface Geom { id: string; x1: number; y1: number; x2: number; y2: number } // CSS px

const withAlpha = (hex: string, a: number): string => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
};

class RectRenderer implements IPrimitivePaneRenderer {
  constructor(private readonly parent: RectangleZonePrimitive) {}

  draw(target: CanvasRenderingTarget2D): void {
    const { chart, series, zones } = this.parent;
    if (!chart || !series || !zones.length) return;
    const ts = chart.timeScale();

    const geoms: Geom[] = [];
    target.useBitmapCoordinateSpace(({ context: ctx, bitmapSize, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      ctx.save();
      const W = bitmapSize.width;
      const H = bitmapSize.height;
      for (const z of zones) {
        const xlRaw = ts.timeToCoordinate(Math.min(z.t1, z.t2) as Time);
        const xrRaw = ts.timeToCoordinate(Math.max(z.t1, z.t2) as Time);
        // Partially off-screen zones clamp to the pane edges (OrderBlock pattern);
        // fully unresolvable (both ends outside the loaded range) → skip.
        if (xlRaw == null && xrRaw == null) continue;
        let xl = xlRaw == null ? 0 : Number(xlRaw) * hpr;
        let xr = xrRaw == null ? W : Number(xrRaw) * hpr;
        xl = Math.max(0, Math.min(W, xl));
        xr = Math.max(0, Math.min(W, xr));
        if (xr - xl < 1) continue;

        const yTopR = series.priceToCoordinate(Math.max(z.p1, z.p2));
        const yBotR = series.priceToCoordinate(Math.min(z.p1, z.p2));
        if (yTopR == null || yBotR == null) continue;
        let yT = Number(yTopR) * vpr;
        let yB = Number(yBotR) * vpr;
        yT = Math.max(0, Math.min(H, yT));
        yB = Math.max(0, Math.min(H, yB));
        if (yB - yT < 1) continue;

        const selected = this.parent.selectedId === z.id;
        ctx.fillStyle = withAlpha(z.color, Math.max(0.03, Math.min(0.8, z.opacity)));
        ctx.fillRect(xl, yT, xr - xl, yB - yT);
        ctx.lineWidth = Math.max(1, Math.round((selected ? 2 : 1) * Math.min(hpr, vpr)));
        ctx.strokeStyle = withAlpha(z.color, selected ? 0.95 : 0.55);
        ctx.setLineDash([]);
        ctx.strokeRect(xl, yT, xr - xl, yB - yT);

        // Selected: small square handles at the top/bottom edge midpoints.
        if (selected) {
          const hs = 4 * Math.min(hpr, vpr);
          const cx = (xl + xr) / 2;
          ctx.fillStyle = withAlpha(z.color, 0.95);
          ctx.fillRect(cx - hs, yT - hs, hs * 2, hs * 2);
          ctx.fillRect(cx - hs, yB - hs, hs * 2, hs * 2);
        }

        geoms.push({ id: z.id, x1: xl / hpr, y1: yT / vpr, x2: xr / hpr, y2: yB / vpr });
      }
      ctx.restore();
    });
    this.parent.lastGeoms = geoms;
  }
}

class RectPaneView implements IPrimitivePaneView {
  constructor(private readonly parent: RectangleZonePrimitive) {}
  zOrder() { return 'bottom' as const; }
  renderer(): IPrimitivePaneRenderer | null {
    if (!this.parent.chart || !this.parent.series) return null;
    return new RectRenderer(this.parent);
  }
}

export class RectangleZonePrimitive implements ISeriesPrimitive<Time> {
  zones: RectZone[] = [];
  selectedId: string | null = null;
  chart: IChartApi | null = null;
  series: ISeriesApi<SeriesType> | null = null;
  /** Rendered geometry (CSS px) from the last draw — feeds hitTest. */
  lastGeoms: Geom[] = [];
  private readonly _paneViews: RectPaneView[];

  constructor(zones: RectZone[]) {
    this.zones = zones;
    this._paneViews = [new RectPaneView(this)];
  }

  attached(params: SeriesAttachedParameter<Time>): void {
    this.chart = params.chart as IChartApi;
    this.series = params.series as ISeriesApi<SeriesType>;
  }
  detached(): void {
    this.chart = null;
    this.series = null;
    this.lastGeoms = [];
  }

  updateAllViews(): void { /* renderer reads state fresh each draw */ }
  paneViews(): readonly IPrimitivePaneView[] { return this._paneViews; }

  setData(zones: RectZone[], selectedId: string | null): void {
    this.zones = zones;
    this.selectedId = selectedId;
    this.chart?.applyOptions({});
  }

  /** Hit-test a CSS-px point against the last-rendered geometry. Edges (±5px)
   *  win over body so resize is reachable inside thin zones; topmost (last
   *  drawn) zone wins overlaps. (Named hitTestZone: `hitTest` is a reserved
   *  ISeriesPrimitive member with a different signature.) */
  hitTestZone(x: number, y: number): RectHit | null {
    const TOL = 5;
    for (let i = this.lastGeoms.length - 1; i >= 0; i--) {
      const g = this.lastGeoms[i];
      const inX = x >= g.x1 - TOL && x <= g.x2 + TOL;
      const inY = y >= g.y1 - TOL && y <= g.y2 + TOL;
      if (!inX || !inY) continue;
      // Corners resolve to the horizontal edge (top/bottom win over left/right).
      if (Math.abs(y - g.y1) <= TOL) return { id: g.id, part: 'top' };
      if (Math.abs(y - g.y2) <= TOL) return { id: g.id, part: 'bottom' };
      if (Math.abs(x - g.x1) <= TOL) return { id: g.id, part: 'left' };
      if (Math.abs(x - g.x2) <= TOL) return { id: g.id, part: 'right' };
      if (y > g.y1 && y < g.y2 && x > g.x1 && x < g.x2) return { id: g.id, part: 'body' };
    }
    return null;
  }
}
